import { useEffect, useState } from "react";

interface DemoPlayerProps {
  title: string;
  cover: string;
  isLive?: boolean;
}

export default function DemoPlayer({ title, cover, isLive = false }: DemoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(isLive ? 68 : 12);

  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => {
      setProgress((p) => (p >= 100 ? (isLive ? 68 : 0) : p + 0.5));
    }, 500);
    return () => clearInterval(timer);
  }, [playing, isLive]);

  return (
    <div className="w-full bg-black rounded-lg overflow-hidden">
      <div className="relative w-full aspect-video">
        <img
          src={cover}
          alt={`${title} 演示播放器画面`}
          title={`${title} 在线观看`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/45" />

        <div className="absolute top-3 left-3 flex items-center gap-2">
          {isLive && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-accent-500 text-white text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse-live" />
              直播中
            </span>
          )}
          <span className="px-2.5 py-1 rounded-md bg-black/50 text-white/80 text-xs backdrop-blur">
            演示播放器
          </span>
        </div>

        {!playing && (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label="播放"
            className="absolute inset-0 flex items-center justify-center cursor-pointer group"
          >
            <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center text-primary-700 transition-colors">
              <i className="ri-play-fill text-3xl md:text-4xl" />
            </span>
          </button>
        )}

        {playing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 pointer-events-none">
            <i className="ri-live-line text-3xl text-white/70" />
            <p className="mt-2 text-white text-sm md:text-base font-medium">
              演示播放中 · {title}
            </p>
            <p className="mt-1 text-white/60 text-xs">
              当前为前端演示区域，暂无真实直播流接口
            </p>
          </div>
        )}

        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent px-3 md:px-4 pt-6 pb-3">
          <div className="h-1 rounded-full bg-white/25 overflow-hidden mb-3">
            <div
              className="h-full bg-accent-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPlaying((p) => !p)}
                aria-label={playing ? "暂停" : "播放"}
                className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className={playing ? "ri-pause-fill text-lg" : "ri-play-fill text-lg"} />
              </button>
              <button
                type="button"
                onClick={() => setMuted((m) => !m)}
                aria-label={muted ? "取消静音" : "静音"}
                className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className={muted ? "ri-volume-mute-line" : "ri-volume-up-line"} />
              </button>
              <span className="text-white/70 text-xs hidden sm:inline">
                {isLive ? "LIVE" : `${Math.round(progress)}%`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-white/50 text-xs hidden md:inline">1080P · 高清</span>
              <button
                type="button"
                aria-label="全屏"
                className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-fullscreen-line" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}